import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useGetProfile } from '../../../globals';
import { getCashMode, getCashModeHistory } from '../../../utils/service-calls';

export const CASH_MODE_ENUM = {
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
};

const CashModeContext = createContext();

export const useCashModeContext = () => useContext(CashModeContext);

function CashModeProvider({ children, mockedValue }) {
  // const cachedCashMode = getItemFromSessionStorage(CACHED_CASH_MODE_KEY) || null;
  const [cashMode, setCashMode] = useState(null);
  const [cashModeError, setCashModeError] = useState(null);
  const [athleteInfo, setAthleteInfo] = useState(null);
  const [cashModeCreateTime, setCashModeCreateTime] = useState(null);
  const [previousAthleteInfo, setPreviousAthleteInfo] = useState(null);
  const [previousCreateTime, setPreviousCreateTime] = useState(null);
  const { storeId } = useGetProfile();

  const fetchCashMode = React.useCallback(() => {
    if (!storeId) {
      return Promise.reject(new Error('No storeId available'));
    }

    return getCashMode(storeId)
      .then((cashModeResponse) => {
        const { athleteName, athleteNumber, createTime, cash_mode: mode } = cashModeResponse.data;

        if (!(mode in CASH_MODE_ENUM)) {
          throw new Error(`Cash mode value: ${mode} does not match key in CASH_MODE_ENUM`);
        }

        // Try to fetch history for rolling indicator, but don't fail if endpoint doesn't exist
        return getCashModeHistory(storeId)
          .then((historyResponse) => {
            const { currentStatus, previousStatus } = historyResponse.data;
            setCashMode(mode);

            // Set current status from history
            if (currentStatus) {
              setAthleteInfo(`${currentStatus.athleteName} (${currentStatus.athleteNumber})`);
              setCashModeCreateTime(currentStatus.createTime);
            }

            // Set previous status from history
            if (previousStatus) {
              setPreviousAthleteInfo(
                `${previousStatus.athleteName} (${previousStatus.athleteNumber})`,
              );
              setPreviousCreateTime(previousStatus.createTime);
            } else {
              setPreviousAthleteInfo(null);
              setPreviousCreateTime(null);
            }
            return cashModeResponse;
          })
          .catch(() => {
            // History endpoint not available, fallback to current data only
            setCashMode(mode);
            setAthleteInfo(`${athleteName} (${athleteNumber})`);
            setCashModeCreateTime(createTime);
            setPreviousAthleteInfo(null);
            setPreviousCreateTime(null);
            return cashModeResponse;
          });
      })
      .catch((err) => {
        setCashModeError(err);
        throw err;
      });
  }, [storeId]);

  useEffect(() => {
    // Reset state when storeId is cleared (logout scenario)
    if (!storeId) {
      setCashMode(null);
      setCashModeError(null);
      setAthleteInfo(null);
      setCashModeCreateTime(null);
      setPreviousAthleteInfo(null);
      setPreviousCreateTime(null);
      return;
    }

    fetchCashMode();
  }, [storeId, fetchCashMode]);

  // TODO: Will be refactored to redux in near future
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    athleteInfo,
    cashMode,
    cashModeCreateTime,
    cashModeError,
    fetchCashMode,
    previousAthleteInfo,
    previousCreateTime,
    setCashModeError,
  };

  return (
    <CashModeContext.Provider value={mockedValue || value}>{children}</CashModeContext.Provider>
  );
}

CashModeProvider.defaultProps = {
  children: <div />,
  mockedValue: null,
};

CashModeProvider.propTypes = {
  children: PropTypes.node,
  // only used for testing
  // eslint-disable-next-line react/forbid-prop-types
  mockedValue: PropTypes.any,
};

export default CashModeProvider;
